import React, { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import './AccountPage.css';
import AccountContext from '../../AccountContext';


const ENEMY_GIF = {
    spincrypt: require('./EnemyGifs/Spincrypt.gif'),
    hashcoil: require('./EnemyGifs/Hashcoil.gif'),
    bitforge: require('./EnemyGifs/Bitforge.gif'),
    forgevault: require('./EnemyGifs/Forgevault.gif'),
    bytecore: require('./EnemyGifs/Bytecore.gif'),
    cryptorot: require('./EnemyGifs/Cryptorot.gif'),
    satoburst: require('./EnemyGifs/Satoburst.gif'),
    shockshroom: require('./EnemyGifs/Shockshroom.gif'),
    bitmite: require('./EnemyGifs/Bitmite.gif'),
    cryptogolem: require('./EnemyGifs/Cryptogolem.gif'),
    blockdaemon: require('./EnemyGifs/Blockdaemon.gif'),
    nodereaper: require('./EnemyGifs/Nodereaper.gif'),
    hashwatcher: require('./EnemyGifs/Hashwatcher.gif'),
    seedflare: require('./EnemyGifs/Seedflare.gif'),
    sigmashock: require('./EnemyGifs/Sigmashock.gif'),
    bladebyte: require('./EnemyGifs/Bladebyte.gif'),
    bytepiercer: require('./EnemyGifs/Bytepiercer.gif'),
    chainripper: require('./EnemyGifs/Chainripper.gif'),
    cryptarrow: require('./EnemyGifs/Cryptarrow.gif'),
    nodebruiser: require('./EnemyGifs/Nodebruiser.gif'),
    chainblast: require('./EnemyGifs/Chainblast.gif'),
    nodeseeker: require('./EnemyGifs/Nodeseeker.gif'),
    scriptfang: require('./EnemyGifs/Scriptfang.gif'),
    hashsentry: require('./EnemyGifs/Hashsentry.gif'),
    byteguard: require('./EnemyGifs/Byteguard.gif'),
    ghostbit: require('./EnemyGifs/Ghostbit.gif'),
    chainlase: require('./EnemyGifs/Chainlase.gif'),
    txncleaver: require('./EnemyGifs/Txncleaver.gif'),
    merklenode: require('./EnemyGifs/Merklenode.gif'),
    bitshard: require('./EnemyGifs/Bitshard.gif'),
    bittank: require('./EnemyGifs/Bittank.gif'),
    blockbyte: require('./EnemyGifs/Blockbyte.gif'),
    blocksting: require('./EnemyGifs/Blocksting.gif'),
    cryptaspike: require('./EnemyGifs/Cryptaspike.gif'),
    merklecoil: require('./EnemyGifs/Merklecoil.gif'),
    merkleshard: require('./EnemyGifs/Merkleshard.gif'),
};

const defaultGeneralStats = {};
const defaultEnemiesKills = {};
const defaultBossKills = {};

const ELLIPSIS = '�';
const shorten = (addr) =>
    typeof addr === 'string' && addr.length > 10
        ? `${addr.slice(0, 6)}${ELLIPSIS}${addr.slice(-4)}`
        : addr ?? '_';

/** Always show first 6, then exactly "***", then last 4 (if long enough) */
function maskWalletAddress(address) {
    if (typeof address !== 'string') return '';
    if (address.length <= 10) return address;
    const start = address.slice(0, 6);
    const end = address.slice(-4);
    return `${start}***${end}`;
}

function toLabel(str = '') {
    return String(str)
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (l) => l.toUpperCase());
}

const number = (v) => (typeof v === 'number' ? v : Number(v || 0));

/** Compute a cool warm color for kills; 0  blue(210deg), 1e6  red(0deg) */
function getKillsStyle(killsRaw) {
    const kills = number(killsRaw);
    const max = 1_000_000;
    const t = Math.max(0, Math.min(1, kills / max));
    const hue = 210 - 210 * t; // 210 (blue)  0 (red)
    const color = `hsl(${hue} 100% 62%)`;
    const glow1 = 6 + 10 * t;
    const glow2 = 12 + 16 * t;
    const alpha1 = 0.6 + 0.25 * t;
    const alpha2 = 0.25 + 0.25 * t;
    const shadow = `0 0 ${glow1}px hsla(${hue},100%,60%,${alpha1}), 0 0 ${glow2}px hsla(${hue},100%,60%,${alpha2})`;
    return { color, textShadow: shadow, fontWeight: 900 };
}

export default function AccountPage() {
    const { accountData, loading, error } = useContext(AccountContext) || {};
    const [tab, setTab] = useState('general'); // 'general' | 'fighters' | 'bosses'

    const [previewEnemy, setPreviewEnemy] = useState(null); // { key, count, src } | null
    const openPreview = useCallback((enemyKey, count) => {
        const src = ENEMY_GIF[enemyKey];
        setPreviewEnemy({ key: enemyKey, count, src: src || null });
    }, []);
    const closePreview = useCallback(() => setPreviewEnemy(null), []);

    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && closePreview();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [closePreview]);

    const generalStats = useMemo(
        () => accountData?.account_overview || defaultGeneralStats,
        [accountData]
    );
    const enemiesKills = useMemo(
        () => accountData?.enemies_kills || defaultEnemiesKills,
        [accountData]
    );
    const bossKills = useMemo(
        () => accountData?.boss_kills || defaultBossKills,
        [accountData]
    );

    // Compose �hero� cards for General tab (order + labels tuned)
    const heroCards = useMemo(() => {
        const o = generalStats || {};
        return [
            { label: 'Account Career Ammo Used', value: number(o.account_career_ammo_used) },
            { label: 'Account Career Kills', value: number(o.account_career_kills) },
            { label: 'Account Deaths', value: number(o.account_deaths) },
            { label: 'Account Experience', value: number(o.account_experience) },
            { label: 'Account Games Played', value: number(o.account_games_played) },
            { label: 'Account Level', value: number(o.account_level) },

        ].filter((c) => !Number.isNaN(c.value));
    }, [generalStats]);

    return (
        <div className="ap-container">
            {/* Wallet banner + tabs */}
            <div className="ap-header">
                <div className="ap-wallet">
                    <div className="ap-wallet-label">Wallet:</div>
                    <div className="ap-wallet-value" title={generalStats.wallet_address || ''}>
                        {maskWalletAddress(generalStats.wallet_address)}
                    </div>
                </div>

                <div className="ap-tabs" role="tablist" aria-label="Account views">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'general'}
                        className={`ap-tab ${tab === 'general' ? 'active' : ''}`}
                        onClick={() => setTab('general')}
                    >
                        General Info
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'fighters'}
                        className={`ap-tab ${tab === 'fighters' ? 'active' : ''}`}
                        onClick={() => setTab('fighters')}
                    >
                        Fighter Kills
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'bosses'}
                        className={`ap-tab ${tab === 'bosses' ? 'active' : ''}`}
                        onClick={() => setTab('bosses')}
                    >
                        Boss Kills
                    </button>
                </div>
            </div>

            <div className="ap-body">
                {loading ? (
                    <div className="ap-empty">Loading account data�</div>
                ) : error ? (
                    <div className="ap-error">{error}</div>
                ) : tab === 'general' ? (
                    <>
                        <div className="ap-section-title">General Info</div>

                        {/* Stat cards grid */}
                        <div className="ap-grid">
                            {heroCards.length === 0 ? (
                                <div className="ap-empty">No account stats available.</div>
                            ) : (
                                heroCards.map((c) => {
                                    const extraClass =
                                        c.label === 'Account Deaths'
                                            ? 'ap-val-deaths'
                                            : c.label === 'Account Experience'
                                                ? 'ap-val-exp'
                                                : c.label === 'Account Level'
                                                    ? 'ap-val-level'
                                                    : '';

                                    // dynamic gradient for Account Career Kills
                                    const dynamicStyle =
                                        c.label === 'Account Career Kills' ? getKillsStyle(c.value) : undefined;

                                    return (
                                        <div key={c.label} className="ap-card">
                                            <div className="ap-card-label">{c.label}</div>
                                            <div className={`ap-card-value ${extraClass}`} style={dynamicStyle}>
                                                {Number(c.value || 0).toLocaleString()}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </>
                ) : tab === 'fighters' ? (
                    <>
                        <div className="ap-section-title">Fighter Kills</div>

                        <div className="ap-table-card">
                            <div className="ap-table-title">Enemy Types</div>
                            <div className="ap-table">
                                <div className="ap-thead">
                                    <div>Name</div>
                                    <div className="ap-num">Kills</div>
                                </div>

                                <div className="ap-tbody">
                                    {Object.keys(enemiesKills || {}).length === 0 ? (
                                        <div className="ap-empty ap-inner">No fighter kills yet.</div>
                                    ) : (
                                        Object.entries(enemiesKills)
                                            .filter(([, v]) => number(v) > 0)
                                            .sort((a, b) => number(b[1]) - number(a[1]))
                                            .map(([name, val]) => (
                                                <button
                                                    key={name}
                                                    type="button"
                                                    className="ap-row ap-row-btn"
                                                    onClick={() => openPreview(name, val)}
                                                    title="Click to preview enemy GIF"
                                                >
                                                    <div className="ap-name">{toLabel(name)}</div>
                                                    <div className="ap-num">{number(val).toLocaleString()}</div>

                                                    <span className="ap-rail" aria-hidden="true" />
                                                    <span className="ap-sheen" aria-hidden="true" />
                                                </button>
                                            ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="ap-section-title">Boss Kills</div>

                        <div className="ap-table-card">
                            <div className="ap-table-title">Bosses</div>
                            <div className="ap-table">
                                <div className="ap-thead">
                                    <div>Name</div>
                                    <div className="ap-num">Kills</div>
                                </div>

                                <div className="ap-tbody">
                                    {Object.keys(bossKills || {}).length === 0 ? (
                                        <div className="ap-empty ap-inner">No boss kills yet.</div>
                                    ) : (
                                        Object.entries(bossKills)
                                            .filter(([, v]) => number(v) > 0)
                                            .sort((a, b) => number(b[1]) - number(a[1]))
                                            .map(([name, val]) => (
                                                <div key={name} className="ap-row">
                                                    <div className="ap-name ap-name-boss">{toLabel(name)}</div>
                                                    <div className="ap-num ap-num-boss">
                                                        {number(val).toLocaleString()}
                                                    </div>

                                                    <span className="ap-rail" aria-hidden="true" />
                                                    <span className="ap-sheen" aria-hidden="true" />
                                                </div>
                                            ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* GIF Preview modal */}
            {previewEnemy && (
                <div className="ap-gif-overlay" onClick={closePreview}>
                    <div
                        className="ap-gif-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-label={`${toLabel(previewEnemy.key)} preview`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ap-gif-head">
                            <div className="ap-gif-title">
                                {toLabel(previewEnemy.key)} - {Number(previewEnemy.count || 0).toLocaleString()} kills
                            </div>
                            <button className="ap-gif-close" type="button" onClick={closePreview} title="Close">
                                CLOSE
                            </button>
                        </div>

                        {previewEnemy.src ? (
                            <img
                                className="ap-gif-img"
                                src={previewEnemy.src}
                                alt={`${toLabel(previewEnemy.key)} animation`}
                                draggable="false"
                            />
                        ) : (
                            <div className="ap-gif-missing">
                                <div className="ap-gif-missing-title">No Preview</div>
                                <div className="ap-gif-missing-sub">
                                    No GIF found for <strong>{toLabel(previewEnemy.key)}</strong>.
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
